<template>
  <div class="container py-14 lg:py-24">
    <div class="flex flex-col justify-between lg:flex-row lg:items-center">
      <Heading
        :semantic="isSemantic"
        :level="level"
        class="pb-10 text-3xl font-normal text-blue-950 lg:pb-14 lg:text-6xl"
      >
        {{ data.title ?? $t('Similar products') }}
      </Heading>
      <div v-if="data.button?.length" class="mb-6 lg:mb-10">
        <Button
          v-for="button in data.button"
          :key="button.id"
          :label="button.title"
          :icon="button.icon"
          :target="button.externalLink ? '_blank' : undefined"
          :to="
            button.pageLink !== null
              ? localePathByType(
                  button.pageLink?.__typename as string,
                  button.pageLink?.slug as string
                )
              : button.externalLink
          "
        />
      </div>
    </div>
    <div class="gap-pull">
      <Swiper
        class="lg:h-[520px]"
        :slides-per-view="1.2"
        :slides-offset-before="16"
        :space-between="32"
        :breakpoints="{
          460: {
            slidesPerView: 1.8
          },
          768: {
            slidesPerView: 2.2
          },
          1024: {
            slidesOffsetBefore: 0,
            slidesPerView: 4
          }
        }"
      >
        <SwiperSlide v-for="(product, index) in relatedArticles" :key="index" class="rounded-2xl">
          <ProductCard :data="product" />
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { SectionRelatedShopProductsListRecord, ShopProductRecord } from '~/types/generated'
import 'swiper/css'

const { locale } = useI18n()
const { localePathByType } = useLocalePathByType()

const props = defineProps({
  data: {
    type: Object as PropType<SectionRelatedShopProductsListRecord>,
    default: () => {}
  },
  index: {
    type: Number,
    required: true
  }
})

const { level, isSemantic } = useSectionRules(props.index, !!props.data.title)

const relatedArticles = ref<ShopProductRecord[]>([])

const filterAny = props.data.categories?.length
  ? props.data.categories?.map((x: { id: any }) => x.id)
  : []

const { data: articles } = await useAsyncGql('shopProducts', {
  locale: locale.value,
  first: 4,
  anyIn: filterAny
})

if (articles.value) {
  relatedArticles.value = articles.value.allShopProducts
}

// Add CMS Overrides
if (Array.isArray(props.data.overrideArticles) && props.data.overrideArticles.length) {
  relatedArticles.value.unshift(...props.data.overrideArticles)
  relatedArticles.value = relatedArticles.value.slice(0, 4)
}
</script>
